import { useContext, useEffect, useState } from 'react';
import InsuranceProductsMenuHeader from './InsuranceProductsMenuHeader';
import InsurancePolicyModal from './insuranceCreationAndEdit/InsurancePolicyModal';
import { InsurancePolicy, InsurancePolicyMainBranch } from '../../../interfaces/InsurancePolicy';
import { InsuranceAppendix } from '../../../interfaces/InsuranceAppendix';
import { getInsurancePolicies } from '../../../utils/api/insurancePolicy';
import { getInsuranceAppendices } from '../../../utils/api/insuranceAppendix';
import { UserProfileContext } from '../../../context/userProfileContext';
import { createNewAppendix, createNewPolicy } from './insuranceCreationAndEdit/appendixAndPolicyCreationFunctions';
import { ProductsContext } from './insuranceProductsContext';
import AppendixCreationModal from './insuranceCreationAndEdit/AppendixCreationAndEditingModal';
import { initialInsuranceAppendixState } from './insuranceCreationAndEdit/initialPolicyAndAppendixState';
import { getClientFamilyMembers } from '../../../utils/getClientFamilyMembers';
import Table from '../../../components/Table';
import PolicyRowTableExpansion from './PolicyRowTableExpansion';
import { getCompanies } from '../../../utils/api/companies';
import { Company } from '../../../interfaces/Company';
import { useEnumsContext } from '../../../context/EnumsContext';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { Insurance, InsuranceRecommendation, InsuranceMiniPreferredTypes } from '../../../interfaces/Insurance';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import {
    PolicyCompanyNameColumn,
    PolicyPremiumColumn,
    PolicyNumberColumn,
    PolicyCoverageAmountColumn,
    PolicyPlanTypeColumn,
    PolicyRecommendationColumn,
    PolicyIsAgentAppointedColumn,
    PolicyActionsColumn,
    PolicyTermColumn,
    PolicyInsuredListColumn,
} from './insuranceProductColumns';
import { PolicyMainBranchColumn } from './insuranceProductColumns';
import TranslatedColumn from '../../../components/TranslatedColumn';
import { useTranslation } from 'react-i18next';
import './insurance.scss';
import Loader from '../../../components/Loader';
import { Client } from '../../../interfaces/Client';

const InsuranceProducts = () => {
    const { t: t1 } = useTranslation('insurance');
    const { t: t2 } = useTranslation('insurancePolicies');
    const t = (key: string) => (t1(key) !== key ? t1(key) : t2(key));

    const [sortBy, setSortBy] = useState<string>('');
    const { profileContextData } = useContext(UserProfileContext);
    const { client, currentRound, selectedModule } = profileContextData;

    const insurance: Insurance = profileContextData.currentRound as Insurance;

    const [showNewProductCreationModal, setShowNewProductCreationModal] = useState(false);
    const [showNewAppendixCreationModal, setShowNewAppendixCreationModal] = useState(false);

    const [insurancePolicies, setInsurancePolicies] = useState<InsurancePolicy[]>([]);

    const [filterByPolicyNumbers, setFilterByPolicyNumbers] = useState<string[]>([]);
    const [filteredInsurancePolicies, setFilteredInsurancePolicies] = useState<InsurancePolicy[]>([]);

    const [initiallyExpandedRows, setInitiallyExpandedRows] = useState<string[]>([]);
    const [showGenerateForms, setShowGenerateForms] = useState(false);
    const [showQuestionnaireForms, setShowQuestionnaireForms] = useState(false);
    const [consts, setConsts] = useState<any>({});
    const [noPoliciesForClient, setNoPoliciesForClient] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const enums = useEnumsContext();

    useEffect(() => {
        const assignConsts = async () => {
            if (enums && enums.RecommendationReasons && enums.InsuranceRecommendation) {
                try {
                    const { companies } = await getCompanies({ type: 'insurance' });

                    const newConsts = {
                        companies,
                        RecommendationReasons: enums.RecommendationReasons,
                        InsuranceRecommendation: enums.InsuranceRecommendation,
                        familyMembers: insurance.familyMembers,
                    };

                    setConsts(newConsts);
                } catch (error) {
                    globalErrorHandler(error);
                }
            }
        };

        assignConsts();
    }, [enums]);

    // Data creation
    const fullProductCreationHandler = async (newInsurancePolicy: InsurancePolicy) => {
        const newPolicy = await createNewPolicy(newInsurancePolicy);
        setShowNewProductCreationModal(false);
        if (newPolicy) {
            // Reload all policies from the server to ensure correct ordering
            // The sortPoliciesByReplacementOrder function will place replacement policies
            // directly below the cancelled policies they replace
            await getClientsPolicies();
        }
    };

    const getClientsPolicies = async () => {
        try {
            let insuranceId;
            let branches;

            if (selectedModule.pcInsurances?.length) {
                insuranceId = profileContextData.modules.insurances.insurances[0]._id;
                branches = [InsurancePolicyMainBranch.carInsurance, InsurancePolicyMainBranch.homeInsurance];
            } else {
                insuranceId = currentRound?._id;
                branches = [
                    InsurancePolicyMainBranch.healthInsurance,
                    InsurancePolicyMainBranch.lifeInsurance,
                    InsurancePolicyMainBranch.longTermCare,
                    InsurancePolicyMainBranch.workDisability,
                    InsurancePolicyMainBranch.mortgageInsurance,
                    InsurancePolicyMainBranch.personalInjury,
                    InsurancePolicyMainBranch.criticalIllness,
                    InsurancePolicyMainBranch.dentalInsurance
                ];
            }

            const params = {
                insuranceId,
                branch: branches.join(','),
            };

            setIsLoading(true);

            const response = await getInsurancePolicies(params);
            if (response.noPoliciesForClient) {
                setNoPoliciesForClient(true);
            }
            const policies = response.policies;
            policies.forEach((policy: InsurancePolicy) => {
                policy.clientId = typeof policy.client === 'string' ? policy.client : (policy.client as Client)._id;
            });
            setInsurancePolicies(updatePoliciesWithReplacementInfo(policies));
        } catch (error) {
            globalErrorHandler(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Data fetching
    useEffect(() => {
        // Get insurance policies
        getClientsPolicies();
    }, []);

    // Data filtering
    useEffect(() => {
        const sortedFilteredPolicies = sortPoliciesByReplacementOrder(insurancePolicies);
        setFilteredInsurancePolicies(sortedFilteredPolicies);
    }, [insurancePolicies]);

    useEffect(() => {
        const showGenerateForms = filteredInsurancePolicies.some(
            (policy) =>
                policy.isAgentAppointed ||
                policy.isNewPolicy ||
                (policy.recommendation && policy.recommendation !== InsuranceRecommendation.noChange),
        );
        setShowGenerateForms(showGenerateForms);

        const showQuestionnaireForms = filteredInsurancePolicies.some((policy) => policy.templates?.quastionnaires?.length > 0 && policy.isNewPolicy);
        setShowQuestionnaireForms(showQuestionnaireForms);
    }, [filteredInsurancePolicies]);

    // Helper function to check replacement relationships
    const getReplacementInfo = (policy: InsurancePolicy, rowIndex: number) => {
        const nextPolicy = filteredInsurancePolicies[rowIndex + 1];
        const prevPolicy = filteredInsurancePolicies[rowIndex - 1];

        // Check if this policy is replaced by the next one
        const isReplacedByNext = nextPolicy?.isNewPolicy && 
            nextPolicy?.replacementPolicies?.some((rp: any) => rp._id === policy._id);

        // Check if this policy replaces the previous one
        const replacesPrevious = policy.isNewPolicy && 
            policy.replacementPolicies?.some((rp: any) => rp._id === prevPolicy?._id);

        return { isReplacedByNext, replacesPrevious };
    };

    // Table styling
    const rowStyle = (row: any, rowIndex: number, isExpanded?: boolean) => {
        const policy = row.original;
        const { isReplacedByNext, replacesPrevious } = getReplacementInfo(policy, rowIndex);

        const styles: any = {
            height: '70px',
            position: 'relative' as const,
        };

        if (policy.isNewPolicy) {
            // Softer green for replacement policies
            styles.backgroundColor = 'rgba(198, 236, 227, 0.4)';
            styles.borderLeft = '4px solid #28a745';
        } else if (
            policy.recommendation &&
            (policy.recommendation === InsuranceRecommendation.fullCancellation || policy.recommendation === InsuranceRecommendation.change)
        ) {
            // Softer red for replaced policies
            const bgColor = 'rgba(241, 85, 108, 0.3)';
            styles.borderLeft = '4px solid #dc3545';
            
            // Add zigzag separator at bottom if this policy is replaced by the next one
            if (isReplacedByNext) {
                // Create zigzag pattern - layer it on top of background color
                const zigzagPattern = `repeating-linear-gradient(
                    -45deg,
                    rgba(108, 117, 125, 0.8) 0px,
                    rgba(108, 117, 125, 0.8) 4px,
                    transparent 4px,
                    transparent 8px
                )`;
                // Set background color first, then layer zigzag on top
                styles.backgroundColor = bgColor;
                styles.backgroundImage = zigzagPattern;
                styles.backgroundSize = '8px 4px';
                styles.backgroundPosition = '0 bottom';
                styles.backgroundRepeat = 'repeat-x';
            } else {
                styles.backgroundColor = bgColor;
            }
        }

        return styles;
    };

    const ExpandedRowHandler = ({ row }: any) => {
        if (row.original?._id) {
            return <PolicyRowTableExpansion policy={row.original} appendices={row.original.appendices} />;
        }
    };

    const getRowClass = (row: any) => {
        const policy = row.original;
        const rowIndex = filteredInsurancePolicies.findIndex((p) => p._id === policy._id);
        const { isReplacedByNext, replacesPrevious } = getReplacementInfo(policy, rowIndex);

        let classes = '';
        if (isReplacedByNext) {
            classes += 'policy-replaced-by-next ';
        }
        if (replacesPrevious) {
            classes += 'policy-replaces-previous ';
        }
        return classes;
    };

    useEffect(() => {
        setFilterByPolicyNumbers(insurancePolicies.map((policy) => policy._id!));
    }, [insurancePolicies]);

    const rowNumbering = (row: any) => {
        const policy = row.original;
        const index = (insurancePolicies.findIndex((p) => p._id === policy._id) + 1).toString();
        return index;
    };


    const policyColumns = [
        {
            Header: t('Main Branch'),
            accessor: 'branch',
            classes: 'table-client',
            Cell: PolicyMainBranchColumn,
        },
        {
            Header: t('Company'),
            accessor: 'company',
            classes: 'table-client',
            Cell: PolicyCompanyNameColumn,
        },
        {
            Header: t('Policy Number'),
            accessor: 'policyNumber',
            classes: 'table-client',
            Cell: PolicyNumberColumn,
        },
        {
            Header: t('Persons'),
            accessor: 'insuredList',
            classes: 'table-client',
            Cell: PolicyInsuredListColumn,
        },
        {
            Header: t('Premium'),
            accessor: 'totalPremium',
            classes: 'table-client',
            Cell: PolicyPremiumColumn,
        },

        {
            Header: t('Term'),
            accessor: 'startDate',
            classes: 'table-client',
            Cell: PolicyTermColumn,
        },
        {
            Header: t('Recommendation'),
            accessor: 'recommendation',
            classes: 'table-client',
            Cell: PolicyRecommendationColumn,
        },
        {
            Header: t('AA'),
            accessor: 'isAgentAppointed',
            classes: 'table-client',
            Cell: PolicyIsAgentAppointedColumn,
        },
        {
            Header: t('Status'),
            accessor: 'status',
            classes: 'table-client',
            Cell: TranslatedColumn(t),
        },
        {
            Header: t('Actions'),
            accessor: '_id',
            classes: 'table-client',
            Cell: PolicyActionsColumn,
        },
    ];

    return (
        <ProductsContext.Provider
            value={{
                insurancePolicies,
                setInsurancePolicies: (policies: InsurancePolicy[]) => setInsurancePolicies(updatePoliciesWithReplacementInfo(policies)),
                createInsuranceProduct: fullProductCreationHandler,
                client,
                consts,
                initiallyExpandedRows,
                setInitiallyExpandedRows,
            }}
        >
            <div className="overflow-x-hidden">
                <InsuranceProductsMenuHeader
                    setShowNewProductCreationModal={setShowNewProductCreationModal}
                    clientId={client._id}
                    clientFullName={client.firstName + ' ' + client.lastName}
                    getClientsPolicies={getClientsPolicies}
                    policies={insurancePolicies}
                    showGenerateForms={showGenerateForms}
                    showQuestionnaireForms={showQuestionnaireForms}
                />

                <InsurancePolicyModal
                    mode="create"
                    replacementPolicies={insurancePolicies.filter((policy) => policy.isSelected)}
                    show={showNewProductCreationModal}
                    onHide={() => setShowNewProductCreationModal(false)}
                    productCreationHandler={fullProductCreationHandler}
                />
                {/* <AppendixCreationModal
                    show={showNewAppendixCreationModal}
                    onHide={() => setShowNewAppendixCreationModal(false)}
                    onSubmit={appendixCreationHandler}
                    currentAppendixState={initialInsuranceAppendixState}
                /> */}
                <div className="badges">
                    {' '}
                    {insurance.insurancePreferredTypes?.map((insurancePreferredType) => (
                        <>
                            <span className={`badge ${InsuranceMiniPreferredTypes.includes(insurancePreferredType) ? 'bg-warning' : 'bg-success'} float-Start`}>{camelCaseToText(insurancePreferredType)}</span>{' '}
                        </>
                    ))}
                </div>

                {isLoading && <Loader />}

                {insurancePolicies.length > 0 && (
                    <Table
                        columns={policyColumns}
                        data={filteredInsurancePolicies}
                        isExpandable={true}
                        ExpandedRow={ExpandedRowHandler}
                        rowNumbering={rowNumbering}
                        pagination={false}
                        tableClass="table-nowrap products-table"
                        rowStyle={rowStyle}
                        rowClass={getRowClass}
                        expandArrowDirection="left"
                        initiallyExpandedRows={initiallyExpandedRows}
                    />
                )}

                {insurancePolicies.length === 0 && !isLoading && noPoliciesForClient && (
                    <div className="placehodler">{t('No policies for client')}</div>
                )}

                {insurancePolicies.length === 0 && !isLoading && !noPoliciesForClient && (
                    <div className="placehodler">{t('Policies not ready yet')}</div>
                )}
            </div>
        </ProductsContext.Provider>
    );
};

export default InsuranceProducts;

const updatePoliciesWithReplacementInfo = (policies: InsurancePolicy[]): InsurancePolicy[] => {
    const policiesWithReplacementInfo = policies.map((currentPolicy) => {
        // const policyItReplaces = policies.find(
        //     (otherPolicy) => otherPolicy.replacementPolicies && otherPolicy.replacementPolicies.includes(currentPolicy._id!),
        // );

        // if (policyItReplaces) {
        //     currentPolicy.policyItReplaces = policyItReplaces._id!;
        // }

        return currentPolicy;
    });

    return policiesWithReplacementInfo;
};

const sortPoliciesByReplacementOrder = (policies: InsurancePolicy[]) => {
    const newPolices = policies.filter((policy) => policy.isNewPolicy);
    let otherPolicies = policies.filter((policy) => !policy.isNewPolicy);

    for (const newPolicy of newPolices) {
        // Find the index of the last cancelled policy that this new policy replaces
        // This ensures replacement policies appear directly below the cancelled policy
        let index = -1;
        const replacementPolicyIds = newPolicy.replacementPolicies?.map((p) => p._id) || [];
        
        for (let i = otherPolicies.length - 1; i >= 0; i--) {
            const otherPolicy = otherPolicies[i];
            if (replacementPolicyIds.includes(otherPolicy._id)) {
                index = i;
                break; // Found the last matching policy, place new policy right after it
            }
        }
        
        // If we found a matching policy, insert the new policy right after it
        // Otherwise, append to the end
        if (index >= 0) {
            otherPolicies = [...otherPolicies.slice(0, index + 1), newPolicy, ...otherPolicies.slice(index + 1)];
        } else {
            otherPolicies = [...otherPolicies, newPolicy];
        }
    }

    return otherPolicies;
};
